import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import UserFilter from '../StreamIngest/UserFilter'
import MomentsBrowser from './MomentsBrowser'
import SelectionSpans from './SelectionSpans'

// The moments browser (audio-streaming step 4a): what the selector and the
// judge produced, card by card, and the spans that produced nothing. Its
// metrics — moment latency, the selection backlog, the day's quotes — stay on
// Stream Ingest with the rest of the pipeline's numbers; this page is for
// reading and hearing the output. Wider than most pages, for its two panes.
//
// Two views, each a list beside the one picked from it, under a pinned
// header: the moments, everything cut from the audio in every state, and the
// spans the selector read, with what became of each moment it found. With an
// account picked there is a third, their feed: what their phone shows them
// (their released moments with their friends', newest release first). A link
// from the Users page names the account (`user`, with `name` for the
// filter's label) and the view (`view=feed` or `view=spans`); the URL follows
// the picks.
const viewAt = (searchParams) => {
  const asked = searchParams.get('view')
  if (asked === 'spans') return 'spans'
  if (asked === 'feed' && searchParams.get('user')) return 'feed'
  return 'moments'
}

const Moments = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [user, setUser] = useState(() => {
    const id = searchParams.get('user')
    return id ? { id, username: searchParams.get('name') || undefined } : null
  })
  const [view, setView] = useState(() => viewAt(searchParams))
  const follow = (nextUser, nextView) => {
    const params = {}
    if (nextUser) {
      params.user = nextUser.id
      if (nextUser.username) params.name = nextUser.username
    }
    if (nextView !== 'moments') params.view = nextView
    setSearchParams(params, { replace: true })
  }
  // A feed is somebody's, so clearing the account returns to the moments.
  const pickUser = (next) => {
    const nextView = !next && view === 'feed' ? 'moments' : view
    setUser(next)
    setView(nextView)
    follow(next, nextView)
  }
  const pickView = (next) => {
    setView(next)
    follow(user, next)
  }
  // The title and its view toggle, and the account filter apart from them:
  // each view's header (`FilterHeader`) places it, beside the title on a
  // desktop and behind the Filters button on a phone.
  const title = (
    <>
      <Typography variant="h5" fontWeight={700}>
        Moments
      </Typography>
      <ToggleButtonGroup
        value={view}
        exclusive
        size="small"
        onChange={(event, next) => next && pickView(next)}
      >
        <ToggleButton value="moments">
          {user ? 'Their moments' : 'Moments'}
        </ToggleButton>
        {user && <ToggleButton value="feed">Their feed</ToggleButton>}
        <ToggleButton value="spans">
          {user ? 'Their spans' : 'Spans'}
        </ToggleButton>
      </ToggleButtonGroup>
    </>
  )
  const account = <UserFilter user={user} onChange={pickUser} />
  return view === 'spans' ? (
    <SelectionSpans
      title={title}
      account={account}
      user={user}
      onUser={pickUser}
    />
  ) : (
    <MomentsBrowser
      title={title}
      account={account}
      user={user}
      onUser={pickUser}
      feed={Boolean(user) && view === 'feed'}
    />
  )
}

export default Moments
